import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const HERO_IMG =
  'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=1600&q=80';
// Modern residential high-rise architecture, Adriatic feel

const PROJECT_IMG_1 =
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80';
const PROJECT_IMG_2 =
  'https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=1200&q=80';
const PROJECT_IMG_3 =
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=80';
const ABOUT_IMG =
  'https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1200&q=80';
const TRANSPARENCY_IMG =
  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=80';

export default function Home({ lang }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('contact.validation_required');
    if (!form.email.trim()) e.email = t('contact.validation_required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('contact.validation_email');
    if (!form.message.trim()) e.message = t('contact.validation_required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    // Frontend-only: could store to localStorage. Redirect to Croatian thank-you.
    try {
      const key = 'aurelqen_contact_last';
      localStorage.setItem(key, JSON.stringify({ ...form, ts: Date.now() }));
    } catch (e) {}
    navigate('/thank_you');
  };

  return (
    <main className="aq-home">
      {/* Hero */}
      <section className="aq-hero" id="top">
        <div className="aq-container aq-hero__grid">
          <div className="aq-hero__copy">
            <span className="aq-eyebrow">{t('hero.eyebrow')}</span>
            <h1 className="aq-hero__title">
              <span>{t('hero.title_1')}</span>
              <span className="aq-hero__title-accent">{t('hero.title_2')}</span>
            </h1>
            <p className="aq-hero__lede">{t('hero.lede')}</p>
            <div className="aq-hero__cta">
              <button type="button" className="aq-btn aq-btn--primary" onClick={() => scrollTo('contact')} data-testid="hero-cta-primary">
                {t('hero.cta_primary')}
              </button>
              <button type="button" className="aq-btn aq-btn--ghost" onClick={() => scrollTo('projects')} data-testid="hero-cta-secondary">
                {t('hero.cta_secondary')} →
              </button>
            </div>
            <div className="aq-hero__tags">
              <div><span>{t('hero.tag_1')}</span><b>{t('hero.tag_1_value')}</b></div>
              <div><span>{t('hero.tag_2')}</span><b>{t('hero.tag_2_value')}</b></div>
              <div><span>{t('hero.tag_3')}</span><b>{t('hero.tag_3_value')}</b></div>
            </div>
          </div>
          <div className="aq-hero__frame">
            <div className="aq-hero__imgwrap">
              <img src={HERO_IMG} alt={t('hero.image_alt')} loading="eager" />
              <div className="aq-hero__marker">
                <span className="aq-hero__marker-dot" aria-hidden="true"></span>
                <span>Zagreb · 45.815°N · 15.982°E</span>
              </div>
            </div>
            <div className="aq-hero__caption">
              <span>Aurelqen · 2026</span>
              <span>HR / EN</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="aq-stats">
        <div className="aq-container">
          <h2 className="aq-stats__heading">{t('stats.heading')}</h2>
          <div className="aq-stats__grid">
            {[1, 2, 3, 4].map((i) => (
              <div className="aq-stat" key={i}>
                <b className="aq-stat__value">{t(`stats.item_${i}_value`)}</b>
                <span className="aq-stat__label">{t(`stats.item_${i}_label`)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / What is crowdfunding */}
      <section className="aq-about" id="about">
        <div className="aq-container aq-about__grid">
          <aside className="aq-about__side">
            <span className="aq-eyebrow">{t('about.eyebrow')}</span>
            <div className="aq-about__imgwrap">
              <img src={ABOUT_IMG} alt={t('projects.image_alt')} loading="lazy" />
            </div>
            <blockquote className="aq-about__quote">
              <span>“</span>{t('about.pull_quote')}
            </blockquote>
          </aside>
          <div className="aq-about__body">
            <h2 className="aq-h2">{t('about.heading')}</h2>
            <p>{t('about.p1')}</p>
            <p>{t('about.p2')}</p>
            <p>{t('about.p3')}</p>
            <p>{t('about.p4')}</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="aq-how" id="how">
        <div className="aq-container">
          <header className="aq-section-head">
            <span className="aq-eyebrow">{t('how.eyebrow')}</span>
            <h2 className="aq-h2">{t('how.heading')}</h2>
            <p className="aq-section-lede">{t('how.intro')}</p>
          </header>
          <div className="aq-how__grid">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aq-step">
                <span className="aq-step__kicker">{t(`how.step_${i}_kicker`)}</span>
                <h3 className="aq-step__title">{t(`how.step_${i}_title`)}</h3>
                <p className="aq-step__text">{t(`how.step_${i}_text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="aq-projects" id="projects">
        <div className="aq-container">
          <header className="aq-section-head">
            <span className="aq-eyebrow">{t('projects.eyebrow')}</span>
            <h2 className="aq-h2">{t('projects.heading')}</h2>
            <p className="aq-section-lede">{t('projects.lede')}</p>
          </header>

          <div className="aq-projects__grid">
            <ProjectCard
              img={PROJECT_IMG_1}
              city={t('projects.p_1_city')}
              title={t('projects.p_1_title')}
              desc={t('projects.p_1_desc')}
              yieldLabel={t('projects.p_1_yield')}
              onClick={() => scrollTo('contact')}
              cta={t('hero.cta_primary')}
              testId="project-1"
            />
            <ProjectCard
              img={PROJECT_IMG_2}
              city={t('projects.p_2_city')}
              title={t('projects.p_2_title')}
              desc={t('projects.p_2_desc')}
              yieldLabel={t('projects.p_2_yield')}
              onClick={() => scrollTo('contact')}
              cta={t('hero.cta_primary')}
              testId="project-2"
            />
            <ProjectCard
              img={PROJECT_IMG_3}
              city={t('projects.p_3_city')}
              title={t('projects.p_3_title')}
              desc={t('projects.p_3_desc')}
              yieldLabel={t('projects.p_3_yield')}
              onClick={() => scrollTo('contact')}
              cta={t('hero.cta_primary')}
              testId="project-3"
            />
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="aq-vision">
        <div className="aq-container aq-vision__grid">
          <div className="aq-vision__copy">
            <span className="aq-eyebrow">{t('vision.eyebrow')}</span>
            <h2 className="aq-h2">{t('vision.heading')}</h2>
            <p>{t('vision.p1')}</p>
            <p>{t('vision.p2')}</p>
            <p>{t('vision.p3')}</p>
            <p>{t('vision.p4')}</p>
          </div>
          <div className="aq-vision__points">
            {['a', 'b', 'c'].map((k, i) => (
              <div className="aq-point" key={k}>
                <span className="aq-point__num">0{i + 1}</span>
                <div>
                  <h3>{t(`vision.points.${k}_title`)}</h3>
                  <p>{t(`vision.points.${k}_text`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency */}
      <section className="aq-transparency" id="transparency">
        <div className="aq-container aq-transparency__grid">
          <div className="aq-transparency__imgwrap">
            <img src={TRANSPARENCY_IMG} alt={t('projects.image_alt')} loading="lazy" />
          </div>
          <div className="aq-transparency__body">
            <span className="aq-eyebrow">{t('transparency.eyebrow')}</span>
            <h2 className="aq-h2">{t('transparency.heading')}</h2>
            <p className="aq-section-lede">{t('transparency.lede')}</p>
            <ul className="aq-transparency__list">
              {[1, 2, 3].map((i) => (
                <li key={i}>
                  <h3>{t(`transparency.item_${i}_title`)}</h3>
                  <p>{t(`transparency.item_${i}_text`)}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="aq-faq" id="faq">
        <div className="aq-container">
          <header className="aq-section-head">
            <span className="aq-eyebrow">{t('faq.eyebrow')}</span>
            <h2 className="aq-h2">{t('faq.heading')}</h2>
          </header>
          <div className="aq-faq__list">
            {[1, 2, 3, 4].map((i) => (
              <FaqItem key={i} q={t(`faq.q_${i}`)} a={t(`faq.a_${i}`)} testId={`faq-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="aq-contact" id="contact">
        <div className="aq-container aq-contact__grid">
          <div className="aq-contact__side">
            <span className="aq-eyebrow">{t('contact.eyebrow')}</span>
            <h2 className="aq-h2">{t('contact.heading')}</h2>
            <p>{t('contact.lede')}</p>
            <p className="aq-contact__email">
              {t('contact.alt_email')}{' '}
              <a href={`mailto:${t('footer.email')}`}>{t('footer.email')}</a>
            </p>
            <p className="aq-contact__addr">{t('footer.address')}</p>
          </div>

          <form className="aq-form" onSubmit={onSubmit} noValidate data-testid="contact-form">
            <label className="aq-field">
              <span>{t('contact.name')}</span>
              <input
                type="text"
                name="name"
                placeholder={t('contact.name_placeholder')}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                aria-invalid={!!errors.name}
                data-testid="contact-name"
              />
              {errors.name && <em className="aq-field__err">{errors.name}</em>}
            </label>
            <label className="aq-field">
              <span>{t('contact.email')}</span>
              <input
                type="email"
                name="email"
                placeholder={t('contact.email_placeholder')}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                aria-invalid={!!errors.email}
                data-testid="contact-email"
              />
              {errors.email && <em className="aq-field__err">{errors.email}</em>}
            </label>
            <label className="aq-field">
              <span>{t('contact.message')}</span>
              <textarea
                name="message"
                rows={5}
                placeholder={t('contact.message_placeholder')}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                aria-invalid={!!errors.message}
                data-testid="contact-message"
              />
              {errors.message && <em className="aq-field__err">{errors.message}</em>}
            </label>
            <button type="submit" className="aq-btn aq-btn--primary aq-form__submit" data-testid="contact-submit">
              {t('contact.submit')}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function ProjectCard({ img, city, title, desc, yieldLabel, onClick, cta, testId }) {
  return (
    <article className="aq-project" data-testid={testId}>
      <div className="aq-project__imgwrap">
        <img src={img} alt={title} loading="lazy" />
        <span className="aq-project__yield">{yieldLabel}</span>
      </div>
      <div className="aq-project__body">
        <span className="aq-project__city">{city}</span>
        <h3 className="aq-project__title">{title}</h3>
        <p className="aq-project__desc">{desc}</p>
        <button type="button" className="aq-btn aq-btn--outline" onClick={onClick}>
          {cta} →
        </button>
      </div>
    </article>
  );
}

function FaqItem({ q, a, testId }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className={`aq-faq__item ${open ? 'is-open' : ''}`} data-testid={testId}>
      <button type="button" className="aq-faq__q" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>{q}</span>
        <span className="aq-faq__icon" aria-hidden="true">{open ? '–' : '+'}</span>
      </button>
      {open && <div className="aq-faq__a"><p>{a}</p></div>}
    </div>
  );
}
